import { useLocalStorage, useScript } from "@uidotdev/usehooks";
import { useEffect, useState, useMemo, useRef } from "react";
import { twMerge } from "tailwind-merge";
import clsx from "clsx";

function ErrorIcon({ className = "" }) {
	return (
		<svg className={twMerge("h-5 w-5 ", ...className.split(" "))} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
			<path d="M2.20164 18.4695L10.1643 4.00506C10.9021 2.66498 13.0979 2.66498 13.8357 4.00506L21.7984 18.4695C22.4443 19.6428 21.4598 21 19.9627 21H4.0373C2.54022 21 1.55571 19.6428 2.20164 18.4695Z" stroke="red" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
			<path d="M12 9V13" stroke="red" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
			<path d="M12 17.0195V17" stroke="red" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
		</svg>
	)
}



function Loading({ className = "", stroke = "cyan", fill = "blue" }) {

	return (
		<svg className={twMerge("animate-spin h-5 w-5 text-white", ...className.split(" "))} xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
			<circle className="opacity-25" cx="12" cy="12" r="10" stroke={stroke} strokeWidth="4"></circle>
			<path className="opacity-75" fill={fill} d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
		</svg>
	)

}

import useCamera, { getVideoConfig } from "./hooks/useCamera";
import useDevice from "./hooks/useDevice";

export default function App() {

	const [loading, setLoading] = useState(true);

	const whatdevice = useDevice();
	const isDesktop = whatdevice === "desktop";

	const VIDEO_CONFIG = useMemo(() => {
		return {
			...getVideoConfig(isDesktop)
		}
	}, [isDesktop])

	const faceAPIStatus = useScript(
		`/js/face-api.min.js`,
		{
			removeOnUnmount: true,
		}
	);

	const [registeredFacesLS, setRegisteredFaceLS] = useState(null);

	const parsedRFLS = (registeredFacesLS && (typeof registeredFacesLS === "string" || (Array.isArray(registeredFacesLS) && registeredFacesLS.length > 0))) ? false : true;


	const syncUp = () => {
		setRegisteredFaceLS(localStorage.getItem('registeredFaces') || []);
	}


	const [modelsLoaded, setModelsLoaded] = useState(false);

	const streamObj = useRef(null);

	const videoRef = useRef(null);

	const faceAPIStatusReady = faceAPIStatus === "ready";

	const {
		acquireInputDevice,
		currentPermission,
		defaultCamera,
		getCameraPermission,
		getCameras,
		getDefaultCamera,
		hasCameraPermission,
		isCameraDenied,
		cameraError,
		preferredInputDevice,
		search,
		videoInputDevices
	} = useCamera({
		isDetect: false,
	});

	async function startVideo() {

		let r = false, stream;

		try {
			r = await search();

			stream = await navigator.mediaDevices.getUserMedia(VIDEO_CONFIG);

			// console.log(JSON.stringify(stream))

			streamObj.current = stream;
			// console.log(videoRef.current);
			if (videoRef.current) {
				videoRef.current.srcObject = stream;
			}

		} catch (err) {
			r = false;
			console.error("[Error]", err);
		} finally {
			setLoading(false);
		}

		return r;
	}

	useEffect(() => {

		if (!faceAPIStatusReady || !loading || modelsLoaded) return () => false;

		if (faceAPIStatusReady && !modelsLoaded) {

			Promise.all([
				faceapi.nets.tinyFaceDetector.loadFromUri('/models'),
				faceapi.nets.faceLandmark68Net.loadFromUri('/models'),
				faceapi.nets.faceRecognitionNet.loadFromUri('/models'),
				faceapi.nets.faceExpressionNet.loadFromUri('/models'),
				faceapi.nets.ageGenderNet.loadFromUri('/models') // Add age detection model
			]).then(() => {
				setModelsLoaded(true);
			}).then(startVideo).then(syncUp)
		}

		return () => true;

	}, [faceAPIStatusReady]);

	// console.log(videoRef.current?.width);

	const onRegister = async (evt) => {
		evt.preventDefault();
		evt.stopPropagation();
		const btn = evt.currentTarget;
		try {
			btn.disabled = true;
			
			const name = window.prompt("Enter your name");
			
			if (!name) {
				alert("Please enter a name");
				return;
			}
			
			const detections = await faceapi.detectSingleFace(videoRef.current, new faceapi.TinyFaceDetectorOptions())
				.withFaceLandmarks()
				.withFaceDescriptor()
				.withFaceExpressions()
				.withAgeAndGender();
			
			if (detections) {
				const registeredFaces = JSON.parse(localStorage.getItem('registeredFaces')) || [];
				// Format date part
				const date = new Date().toLocaleDateString('en-IN');
				// Format time part
				const time = new Date().toLocaleTimeString('en-IN');
				
				// Store detected age
				const age = Math.round(detections.age);
				
				// Extract dominant expression
				const expressions = detections.expressions;
				const dominantExpression = Object.keys(expressions).reduce((a, b) => 
					expressions[a] > expressions[b] ? a : b);
				
				if(registeredFaces.length > 0) {
					const labeledDescriptors = registeredFaces.map(f => new faceapi.LabeledFaceDescriptors(f.name, [new Float32Array(f.descriptor)]));
					const faceMatcher = new faceapi.FaceMatcher(labeledDescriptors);
					const bestMatch = faceMatcher.findBestMatch(detections.descriptor);
					
					if (bestMatch.label !== "unknown") {
						alert('Face already registered!');
						return;
					}
				}
				
				registeredFaces.push({ 
					name: name, 
					descriptor: Array.from(detections.descriptor), 
					date, 
					time,
						age,
						expression: dominantExpression // Store dominant expression
				});
				localStorage.setItem('registeredFaces', JSON.stringify(registeredFaces));
				syncUp();
				alert('Face registered successfully!');
			}
		} catch (err) {
			console.error("Error for on Register function", err);
		} finally {
			btn.disabled = false;
		}
	}

	const onRemove = () => {
		
		localStorage.removeItem('registeredFaces');
		syncUp();
	}

	const onDownload = () => {
		const records = JSON.parse(localStorage.getItem('registeredFaces')) || [];

		const csvContent = "data:text/csv;charset=utf-8,"
			+ ["Name,Age,Expression,Date,Time"].concat(records.map(record => 
				`${record.name},${record.age || 'N/A'},${record.expression || 'N/A'},${record.date},${record.time}`)).join("\n");

		const encodedUri = encodeURI(csvContent);
		const link = document.createElement("a");
		link.setAttribute("href", encodedUri);
		link.setAttribute("download", "records.csv");
		document.body.appendChild(link);
		link.click();

		document.body.removeChild(link);
	}

	const onPlay = (evt) => {
		const video = evt.currentTarget;
		const canvas = faceapi.createCanvasFromMedia(video);
		video.parentNode.append(canvas);
		canvas.style.cssText = `z-index: 100;`;


		const displaySize = { width: videoRef.current?.width, height: videoRef.current?.height };
		faceapi.matchDimensions(canvas, displaySize)
		setInterval(async () => {

			const detections = await faceapi.detectSingleFace(video, new faceapi.TinyFaceDetectorOptions())
				.withFaceLandmarks()
				.withFaceDescriptor()
				.withFaceExpressions()
				.withAgeAndGender(); // Add age detection

			if (!detections) return;

			const resizedDetections = faceapi.resizeResults(detections, displaySize)
			canvas.getContext('2d').clearRect(0, 0, canvas.width, canvas.height)
			faceapi.draw.drawDetections(canvas, resizedDetections);
			faceapi.draw.drawFaceLandmarks(canvas, resizedDetections);

			// Extract current dominant expression
			const expressions = detections.expressions;
			const dominantExpression = Object.keys(expressions).reduce((a, b) => 
				expressions[a] > expressions[b] ? a : b);
			
			const registeredFaces = JSON.parse(localStorage.getItem('registeredFaces')) || [];
			syncUp();

			// console.log(detections);

			const age = Math.round(detections.age);

			if (registeredFaces.length === 0) {
				const drawBox = new faceapi.draw.DrawBox(detections?.detection?.box, {
					label: `Unknown, ~${age} years, ${dominantExpression}`,
				});

				drawBox.draw(canvas);
				return;
			}

			const labeledDescriptors = registeredFaces.map(f => new faceapi.LabeledFaceDescriptors(f.name, [new Float32Array(f.descriptor)]));
			const faceMatcher = new faceapi.FaceMatcher(labeledDescriptors);
			const bestMatch = faceMatcher.findBestMatch(detections.descriptor);

			if (bestMatch.label !== "unknown") {
				// Find the registered face to get stored information
				const matchedFace = registeredFaces.find(f => f.name === bestMatch.label);
				const displayAge = matchedFace?.age || age; // Use stored age if available, otherwise use detected age

				
				
				const drawBox = new faceapi.draw.DrawBox(detections?.detection?.box, {
					label: `${bestMatch.label}, ${displayAge} years, ${dominantExpression}`,
				});

				drawBox.draw(canvas);
			} else {
				const drawBox = new faceapi.draw.DrawBox(detections?.detection?.box, {
					label: `Unknown, ~${age} years, ${dominantExpression}`,
				});

				drawBox.draw(canvas);
			}
		}, 100)
	};


	useEffect(() => {

		if (!loading && modelsLoaded && typeof hasCameraPermission === "boolean" && hasCameraPermission === true && videoRef.current) {
			// Then ok

			videoRef.current.addEventListener("play", onPlay);


			return () => {
				videoRef.current.removeEventListener("play", onPlay);
			}
		}

		return () => false;

	}, [loading, modelsLoaded, hasCameraPermission]);



	return (
		<div className="w-full h-dvh flex flex-col items-center justify-center gap-y-5 py-4">
			{(!faceAPIStatusReady || loading) && <Loading className="w-20 h-20" />}
			{
				(!modelsLoaded || !faceAPIStatusReady) && (
					<span className="text-blue-800 font-dm w-full text-center">
						Please wait while we load all the necessary models.<br />This will only take a few seconds.
					</span>
				)
			}
			{currentPermission === "prompt" && modelsLoaded && (
				<span className="text-blue-800 font-dm w-full text-center">
					Please allow the camera/video
					permission from your browser.
				</span>

			)}

			{typeof isCameraDenied === "boolean" &&
				isCameraDenied === true &&
				currentPermission === "denied" && (
					<span className="text-red-400 font-semibold">
						Permission to access your
						camera was denied.
					</span>
				)}
			{
				typeof isCameraDenied === "boolean" && typeof cameraError === "string" && cameraError.length > 0 && (
					<ErrorIcon className="w-20 h-20" />
				)
			}

			{
				typeof isCameraDenied === "boolean" && typeof cameraError === "string" && cameraError.length > 0 && (
					<span className="text-red-400 font-bold font-dm">
						{cameraError}
					</span>
				)
			}

			{(
				<div className={twMerge("flex flex-col items-center justify-center", clsx({
					"hidden": !(!loading && modelsLoaded && typeof hasCameraPermission === "boolean" && hasCameraPermission === true)
				}))}>

					<video
						ref={videoRef}
						autoPlay
						muted
						playsInline
						width={720}
						height={560}
						className={twMerge("absolute rounded-[1rem]", clsx({
							"hidden": !(!loading && modelsLoaded && typeof hasCameraPermission === "boolean" && hasCameraPermission === true)
						}))}
					></video>
				</div>
			)}
			<div className={twMerge("flex flex-row justify-center items-center gap-x-5", clsx({
				"hidden": !(!loading && modelsLoaded && typeof hasCameraPermission === "boolean" && hasCameraPermission === true)
			}))}>
				<button className="px-4 py-3 rounded-[5px] min-w-[100px] text-white font-semibold bg-orange-400" onClick={onRegister}>Register Your Face</button>
				<button className="px-4 py-3 rounded-[5px] min-w-[100px] text-white font-semibold disabled:bg-orange-300 disabled:text-gray-600 bg-orange-400" onClick={onRemove} disabled={parsedRFLS}>Remove All Faces</button>
				<button className="px-4 py-3 rounded-[5px] min-w-[100px] text-white font-semibold disabled:bg-orange-300 disabled:text-gray-600 bg-orange-400" onClick={onDownload} disabled={parsedRFLS}>Export Data</button>
			</div>
			<div className={twMerge("flex flex-row justify-center items-center")}>
				
				
			</div>
		</div>
	)

}
